/**
 * DNS Intelligence type contracts
 * All type definitions for the graph, scan, and enrichment domain.
 * Imported by InfrastructureGraph, NodeDetailPanel, useScanStream.
 */

// ---------------------------------------------------------------------------
// Node / Edge schema
// ---------------------------------------------------------------------------

export type DNSNodeType = 'domain' | 'ip' | 'asn' | 'ns' | 'mx' | 'cname';

export type DNSEdgeType = 'A' | 'AAAA' | 'NS' | 'MX' | 'CNAME';

export interface DNSNode {
  /** Stable unique id: "domain-example.com", "ip-1.2.3.4", "asn-AS15169" */
  id: string;
  type: DNSNodeType;
  label: string;
  /** 0–100. Used for node background color via threatColor(). */
  threatScore: number;
  /** ASN compound parent: "asn-AS{n}". Omit for top-level nodes. */
  parent?: string;
  // Enrichment fields from god daemon
  country?: string;
  org?: string;
  abuse_score?: number;
  feeds?: string[];
  // Raw god daemon payload preserved for NodeDetailPanel
  raw?: Record<string, unknown>;
}

export interface DNSEdge {
  id: string;
  source: string;
  target: string;
  type: DNSEdgeType;
}

// ---------------------------------------------------------------------------
// Cytoscape element wrappers (what cy.add() accepts)
// ---------------------------------------------------------------------------

export interface CytoNodeElement {
  group: 'nodes';
  data: DNSNode;
}

export interface CytoEdgeElement {
  group: 'edges';
  data: DNSEdge;
}

export type CytoElement = CytoNodeElement | CytoEdgeElement;

// ---------------------------------------------------------------------------
// API response types (mirrors api/routes/scan.py ScanJobResponse)
// ---------------------------------------------------------------------------

export interface ScanJobResponse {
  job_id: string;
  status: 'running' | 'completed' | 'failed';
  target: string;
  target_type: 'domain' | 'ip';
  /** god daemon enrichment payload, present when status === 'completed' */
  result: GodEnrichmentResult | null;
  error: string | null;
  created_at: string;       // ISO 8601
  completed_at: string | null;
  /** 0–100 while running, null otherwise */
  progress: number | null;
}

/**
 * Top-level shape returned by god daemon enrich_domain / enrich_ip.
 * Fields are best-effort — presence depends on which plugins ran.
 * Treat all sub-fields as optional.
 */
export interface GodEnrichmentResult {
  domain?: string;
  ip?: string;
  // DNS records
  a_records?: string[];
  aaaa_records?: string[];
  ns_records?: string[];
  mx_records?: Array<{ host: string; priority: number }>;
  cname_records?: string[];
  // IP enrichment
  asn?: string;           // "AS15169"
  asn_org?: string;       // "GOOGLE"
  country?: string;       // "US"
  // Threat scoring
  threat_score?: number;  // 0–100
  abuse_score?: number;
  feeds?: string[];
  // Raw pass-through for NodeDetailPanel
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Threat score → color mapping
// Used in graph node styles and NodeDetailPanel badge
// ---------------------------------------------------------------------------

/**
 * Map a 0–100 threat score to a SOC-palette color.
 *   <20  → green  (clean)
 *   <50  → yellow (suspicious)
 *   <80  → orange (high risk)
 *   ≥80  → red    (malicious)
 */
export function threatColor(score: number): string {
  if (score < 20) return '#56d364';
  if (score < 50) return '#e3b341';
  if (score < 80) return '#f0883e';
  return '#ff4444';
}

// ---------------------------------------------------------------------------
// Graph store types (used by Zustand store in Plan 01-03)
// ---------------------------------------------------------------------------

export interface GraphState {
  elements: CytoElement[];
  selectedNode: DNSNode | null;
  scanning: boolean;
  scanError: string | null;
  currentJobId: string | null;
  scanProgress: number | null;
  lastScanResult: GodEnrichmentResult | null;
  lastScanTarget: string | null;
  scanHistory: import('@/store/graphStore').ScanHistoryEntry[];
}
